import React, { AriaAttributes, ReactNode, useCallback, useMemo, useRef } from "react"

import styled from "styled-components"

import { spacing } from "@izettle/otto"
import { Button } from "@izettle/react"

import { SelectFilesProps } from "@/models"
import { theme } from "@/theme"

import { ErrorsList, FieldErrors } from "./field/field-errors"
import { getErrorListId, getDescriptionId } from "./field/helpers"

const Field = styled.div`
  scroll-margin: ${spacing.xxLarge};

  @media ${theme.devices.desktopWide} {
    scroll-margin: ${spacing.medium};
  }
`

const FileInput = styled.input`
  display: none;
`

const Description = styled.div`
  margin: ${spacing.xxSmall} ${spacing.small} ${spacing.none} ${spacing.small};
`

type FileSelectorProps = {
  selectFiles: SelectFilesProps
  name: string
  type: string
  children: any
  redirect?: string
  isDoubleSided?: boolean
  description?: string
  multiple?: boolean
  variant?: "primary" | "secondary"
  size?: "small" | "regular" | "large"
  errors?: ErrorsList
  help?: ReactNode
}

export const FileSelector = React.forwardRef<HTMLButtonElement, FileSelectorProps>(
  (
    {
      selectFiles,
      name,
      type,
      children,
      redirect = undefined,
      isDoubleSided = false,
      description = "",
      multiple,
      variant = "primary",
      size = "regular",
      errors = [],
      help,
    },
    ref,
  ) => {
    const id = `field_${name}`
    const inputId = `${id}__input`
    const fileInputRef = useRef<HTMLInputElement>(null)

    const hasError = useMemo(() => errors.length > 0, [errors])

    // Browser hack for making the onChange event trigger every time.
    // By browser standard behavior the onChange won't trigger if the same image has been selected earlier (creating a conflict with the "Delete" function from the generic-doc-upload flow)
    const resetElementOnClick = (
      event: React.MouseEvent<HTMLInputElement, MouseEvent>,
    ) => {
      const element = event.target as HTMLInputElement
      element.value = ""
    }

    const clickOnFileInput = useCallback(() => {
      fileInputRef.current?.click()
    }, [fileInputRef?.current])

    const ariaOptionalProps: AriaAttributes = {}
    const ariaDescribedBy: string[] = []

    if (errors.length > 0) ariaDescribedBy.push(getErrorListId(id))
    if (help) ariaDescribedBy.push(getDescriptionId(id))

    if (ariaDescribedBy.length > 0) {
      ariaOptionalProps["aria-describedby"] = ariaDescribedBy.join(" ")
    }

    return (
      <Field aria-controls={`${id} ${inputId}`} data-property-name={name}>
        <Button
          ref={ref}
          id={id}
          fullWidth
          size={size}
          variant={variant}
          {...ariaOptionalProps}
          onClick={clickOnFileInput}
        >
          {children}
        </Button>
        <FileInput
          ref={fileInputRef}
          id={inputId}
          aria-invalid={hasError}
          aria-labelledby={id}
          type="file"
          multiple={multiple}
          onClick={resetElementOnClick}
          tabIndex={-1}
          onChange={(e) =>
            selectFiles(e.target.files, name, type, description, redirect, isDoubleSided)
          }
        />
        <FieldErrors id={id} errorKeys={errors} />
        {help && <Description id={getDescriptionId(id)}>{help}</Description>}
      </Field>
    )
  },
)
